import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '@rocksoft/environment';
import { CategoryType } from '@rocksoft/settings-sdk';
import { categoryTypeLabel, DeviceLinkCategory, DeviceLinkSelection } from '../device-link-selection';

@Component({
  selector: 'era-device-link-selector',
  templateUrl: './device-link-selector.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, FormsModule],
})
export class DeviceLinkSelectorComponent {
  readonly locations = input.required<readonly DeviceLinkCategory[]>();
  readonly childCategories = input.required<readonly DeviceLinkCategory[]>();
  readonly selection = input.required<DeviceLinkSelection>();
  readonly spareOnly = input(false);

  readonly selectionChange = output<DeviceLinkSelection>();

  readonly typeLabel = categoryTypeLabel;

  readonly groups = computed(() => {
    const groups = new Map<CategoryType, DeviceLinkCategory[]>();

    for (const category of this.childCategories()) {
      // Guard: only show child categories of the current selected location
      if (category.parentCategoryId !== this.selection().locationCategoryId) {
        continue;
      }

      // Guard: remove feature flagged categories
      if (category.categoryType == 'Bar' && !environment.features.bar.enabled) {
        continue;
      } else if (category.categoryType == 'FrontDesk' && !environment.features.frontdesk.enabled) {
        continue;
      } else if (category.categoryType == 'Housekeeping' && !environment.features.housekeeping.enabled) {
        continue;
      } else if (category.categoryType == 'Kiosk' && !environment.features.kiosk.enabled) {
        continue;
      } else if (category.categoryType == 'Staff' && !environment.features.staff.enabled) {
        continue;
      }

      const group = groups.get(category.categoryType) ?? [];
      group.push(category);
      groups.set(category.categoryType, group);
    }

    return Array.from(groups, ([type, categories]) => ({
      type,
      categories: categories.sort((a, b) => a.humanId.localeCompare(b.humanId, undefined, { numeric: true })),
    })).sort((a, b) => this.typeLabel(a.type).localeCompare(this.typeLabel(b.type)));
  });

  readonly selectedGroup = computed(() => this.groups().find((group) => group.type === this.selection().categoryType));

  readonly showChildCategoryTarget = computed(() => {
    const group = this.selectedGroup();
    return !!group && (group.type === 'Room' || group.categories.length > 1);
  });

  locationUpdated(locationCategoryId: string): void {
    this.selectionChange.emit(
      this.spareOnly()
        ? { locationCategoryId, categoryType: 'Location', categoryId: locationCategoryId }
        : { locationCategoryId }
    );
  }

  typeUpdated(categoryType: CategoryType): void {
    const locationCategoryId = this.selection().locationCategoryId;
    const group = this.groups().find((entry) => entry.type === categoryType);
    const categoryId =
      categoryType === 'Location'
        ? locationCategoryId
        : categoryType !== 'Room' && group?.categories.length === 1
          ? group.categories[0].id
          : undefined;
    this.selectionChange.emit({ locationCategoryId, categoryType, categoryId });
  }

  targetUpdated(categoryId: string): void {
    this.selectionChange.emit({ ...this.selection(), categoryId });
  }
}
